import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('webmail_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export default function ReferralPage() {
  const { user } = useAuth();
  const [link, setLink] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/referrals/my-link').then(r => setLink(r.data)),
      api.get('/referrals/stats').then(r => setStats(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function copyLink() {
    if (link?.referralUrl) {
      navigator.clipboard.writeText(link.referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function copyShareText() {
    if (link?.shareText) {
      navigator.clipboard.writeText(link.shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Refer & Earn</h1>
      <p className="text-sm text-gray-500 mb-6">
        Share Jubly with friends and colleagues. You both earn ownership points under the Shalom Company Constitution.
      </p>

      {/* Referral Link */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6 mb-6">
        <h2 className="text-sm font-semibold text-indigo-800 mb-3">Your Referral Link</h2>
        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            value={link?.referralUrl || ''}
            readOnly
            className="flex-1 rounded-lg border border-indigo-200 bg-white px-4 py-2.5 text-sm text-gray-800 font-mono"
          />
          <button
            onClick={copyLink}
            className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
          >
            {copied ? '✓ Copied!' : 'Copy'}
          </button>
        </div>
        <button
          onClick={copyShareText}
          className="text-xs text-indigo-600 hover:text-indigo-800"
        >
          Copy share message with link →
        </button>
      </div>

      {/* Points breakdown */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-3xl font-bold text-gray-900">{stats?.totalReferred || 0}</p>
          <p className="text-xs text-gray-500 mt-1">People referred</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-3xl font-bold text-indigo-700">{stats?.totalPoints || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Points earned</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{stats?.pendingActivations || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Pending activation</p>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">How Referral Points Work</h3>
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold">+5</span>
            <p className="text-sm text-gray-700">When someone signs up using your link</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold">+10</span>
            <p className="text-sm text-gray-700">When they're active for 7 days (send an email, create a doc, etc.)</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">+25</span>
            <p className="text-sm text-gray-700">When they upgrade to a paid plan</p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          All referral points count toward the <b>Referrals</b> category (1.0x weight) in the Shalom Company ownership model.
        </p>
      </div>

      {/* Recent events */}
      {stats?.events?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Referral History</h3>
          <div className="space-y-2">
            {stats.events.map((ev, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <span className="text-sm text-gray-800">{ev.referred_name || ev.referred_email?.split('@')[0] || 'Someone'}</span>
                  <span className="ml-2 text-xs text-gray-400">
                    {ev.event_type === 'signup' ? 'signed up' : ev.event_type === 'active_7d' ? 'active 7 days' : ev.event_type === 'upgrade' ? 'upgraded' : ev.event_type}
                  </span>
                </div>
                <span className="text-sm font-semibold text-indigo-600">+{ev.points_awarded} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
